/**
 * Setup Wizard
 *
 * Interactive first-run setup. Detection first, questions only when the
 * software genuinely can't know:
 * 1. Find the app (env var / config / org.json / read-only az lookup; else
 *    ONE question: paste a Client ID or press Enter to create — az login,
 *    app creation, and the consent grant live only inside the create path)
 * 2. Sign in (consent verified by doing — the approval URL appears only if
 *    Microsoft returns AADSTS65001)
 * 3. Discover and select the environment
 * 4. Save the config file
 * 5. Connect the AI app, verify end-to-end, and hand IT the rollout command
 */

import { createInterface } from "readline";
import { existsSync, mkdirSync, readFileSync, writeFileSync, unlinkSync } from "fs";
import { dirname, join } from "path";
import { createAuthProviderFromConfig } from "../auth/device-code.js";
import { probeDeviceCodeEndpoint } from "../auth/device-code-probe.js";
import {
  discoverEnvironments,
  getDefaultEnvironment,
  type DiscoveredEnvironment,
} from "./environment-discovery.js";
import { APP_DISPLAY_NAME, getPermissionSummariesForFeatures } from "./published-app.js";
import { getConfigPath, getConfigDir, hasRunnableConfigSource } from "../config/loader.js";
import {
  DEFAULT_FEATURES,
  FEATURE_DESCRIPTIONS,
  FEATURE_LABELS,
  MINIMAL_FEATURES,
  type FeatureId,
  formatFeatureList,
  isFeatureId,
  normalizeFeatures,
} from "../config/features.js";
import { TOKEN_CACHE_FILE_NAME, ACCOUNT_CACHE_FILE_NAME } from "../utils/constants.js";
import { quietInteractiveLogging } from "../utils/logger.js";
import {
  nativeStorageLoadable,
  repairNativeStorage,
  manualRepairInstruction,
} from "./native-repair.js";
import { readOrgConfig } from "./org-config.js";
import { deleteSecureTokenCache } from "../auth/token-cache.js";
import {
  checkAzureCli,
  ensureAzureCliLogin,
  findExistingApp,
  createAppRegistration,
  addWamRedirectUri,
  runAzCommand,
} from "./app-registration.js";
import {
  appRegistrationExists,
  classifyProvisioningError,
  grantAdminConsent,
  provisionTenantForApp,
  RequiredScopeResolutionError,
  type TenantProvisioningResult,
} from "./graph-provisioning.js";
import {
  type ClientId,
  CLIENT_LABELS,
  connectClient,
  detectClientConfigs,
  detectClaudeCode,
  detectCodex,
} from "./client-config.js";

// ============================================================================
// Console UI - Colors & Styling
// ============================================================================

const c = {
  reset: "\x1b[0m",
  bold: "\x1b[1m",
  dim: "\x1b[2m",
  // Colors
  cyan: "\x1b[36m",
  green: "\x1b[32m",
  yellow: "\x1b[33m",
  red: "\x1b[31m",
  blue: "\x1b[34m",
  magenta: "\x1b[35m",
  white: "\x1b[37m",
  // Backgrounds
  bgBlue: "\x1b[44m",
  bgCyan: "\x1b[46m",
};

const icons = {
  check: `${c.green}✓${c.reset}`,
  cross: `${c.red}✗${c.reset}`,
  info: `${c.blue}i${c.reset}`,
  warn: `${c.yellow}⚠${c.reset}`,
  arrow: `${c.cyan}→${c.reset}`,
  bullet: `${c.dim}•${c.reset}`,
  star: `${c.yellow}★${c.reset}`,
};

// NOTE: readline interface is created inside runSetupWizard, NOT at module load
// This is critical - creating it at module load would consume stdin and break MCP transport

function print(message: string): void {
  console.log(message);
}

function printHeader(): void {
  print("");
  print(
    `${c.cyan}${c.bold}  ╔══════════════════════════════════════════════════════════════╗${c.reset}`,
  );
  print(
    `${c.cyan}${c.bold}  ║${c.reset}                                                              ${c.cyan}${c.bold}║${c.reset}`,
  );
  print(
    `${c.cyan}${c.bold}  ║${c.reset}   ${c.blue}${c.bold}⚡ Power Automate MCP Server${c.reset}                              ${c.cyan}${c.bold}║${c.reset}`,
  );
  print(
    `${c.cyan}${c.bold}  ║${c.reset}   ${c.dim}First-time Setup Wizard${c.reset}                                  ${c.cyan}${c.bold}║${c.reset}`,
  );
  print(
    `${c.cyan}${c.bold}  ║${c.reset}                                                              ${c.cyan}${c.bold}║${c.reset}`,
  );
  print(
    `${c.cyan}${c.bold}  ╚══════════════════════════════════════════════════════════════╝${c.reset}`,
  );
  print("");
}

function printStep(step: number, total: number, message: string): void {
  const progress = "●".repeat(step) + "○".repeat(total - step);
  print("");
  print(`  ${c.cyan}${c.bold}Step ${step}/${total}${c.reset} ${c.dim}${progress}${c.reset}`);
  print(`  ${c.bold}${message}${c.reset}`);
  print(`  ${c.dim}${"─".repeat(56)}${c.reset}`);
}

// ============================================================================
// Token Cache Management
// ============================================================================

/**
 * Clear existing token cache to force fresh authentication
 * This ensures new permissions are picked up after app registration changes
 */
async function clearTokenCache(): Promise<{ cleared: boolean; files: string[] }> {
  const configDir = getConfigDir();
  const files: string[] = [];

  const tokenCachePath = join(configDir, TOKEN_CACHE_FILE_NAME);
  const accountCachePath = join(configDir, ACCOUNT_CACHE_FILE_NAME);

  // On macOS/Windows the tokens live in the platform secure store (keychain/
  // DPAPI) — the files below are just signal/fallback artifacts. Unlinking
  // only the files cleared NOTHING there, so "fresh authentication" silently
  // kept the old tokens. Clear the secure store first.
  const secure = await deleteSecureTokenCache(tokenCachePath);
  if (secure.secureStoreCleared) {
    files.push("secure store");
  }

  if (existsSync(tokenCachePath)) {
    try {
      unlinkSync(tokenCachePath);
      files.push(TOKEN_CACHE_FILE_NAME);
    } catch {
      // Ignore errors - file might be locked
    }
  }

  if (existsSync(accountCachePath)) {
    try {
      unlinkSync(accountCachePath);
      files.push(ACCOUNT_CACHE_FILE_NAME);
    } catch {
      // Ignore errors
    }
  }

  return { cleared: files.length > 0, files };
}

// ============================================================================
// Config helpers
// ============================================================================

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Try to read existing auth config from a saved config file.
 * Returns clientId and tenantId if config exists and has valid UUIDs.
 */
function readExistingAuthConfig(): {
  clientId: string | null;
  tenantId: string | null;
  features: FeatureId[] | null;
} {
  try {
    const configPath = getConfigPath();
    if (!existsSync(configPath)) return { clientId: null, tenantId: null, features: null };
    const raw = readFileSync(configPath, "utf-8");
    const parsed = JSON.parse(raw) as {
      auth?: { clientId?: string; tenantId?: string | null };
      features?: { enabled?: unknown };
    };
    const clientId = parsed?.auth?.clientId;
    const tenantId = parsed?.auth?.tenantId;
    const rawFeatures = Array.isArray(parsed.features?.enabled)
      ? parsed.features.enabled.filter(
          (feature): feature is FeatureId => typeof feature === "string" && isFeatureId(feature),
        )
      : null;
    return {
      clientId: clientId && UUID_RE.test(clientId) ? clientId : null,
      tenantId: tenantId && UUID_RE.test(tenantId) ? tenantId : null,
      features: rawFeatures ? normalizeFeatures(rawFeatures) : null,
    };
  } catch {
    return { clientId: null, tenantId: null, features: null };
  }
}

/** Backwards-compatible helper */
function readExistingClientId(): string | null {
  return readExistingAuthConfig().clientId;
}

// ============================================================================
// Setup state — survives failed runs so re-runs reuse the created app
// ============================================================================

const SETUP_STATE_FILE = "setup-state.json";

interface SetupState {
  createdClientId?: string;
  createdAt?: string;
}

/**
 * The main config is only written on successful completion, so a run that
 * creates an app and then fails (consent, sign-in, propagation) used to lose
 * the clientId — the next run created ANOTHER app, accumulating orphan
 * registrations. The setup-state file persists the created clientId
 * IMMEDIATELY after creation so resumed runs converge on the same app.
 */
function readSetupState(): SetupState {
  try {
    const statePath = join(getConfigDir(), SETUP_STATE_FILE);
    if (!existsSync(statePath)) return {};
    const parsed = JSON.parse(readFileSync(statePath, "utf-8")) as SetupState;
    return {
      createdClientId:
        parsed.createdClientId && UUID_RE.test(parsed.createdClientId)
          ? parsed.createdClientId
          : undefined,
      createdAt: parsed.createdAt,
    };
  } catch {
    return {};
  }
}

function writeSetupState(state: SetupState): void {
  try {
    const configDir = getConfigDir();
    if (!existsSync(configDir)) mkdirSync(configDir, { recursive: true });
    writeFileSync(join(configDir, SETUP_STATE_FILE), JSON.stringify(state, null, 2), {
      encoding: "utf-8",
      mode: 0o600,
    });
  } catch {
    // best-effort — losing this only risks an orphan app on a failed run
  }
}

// ============================================================================
// Feature / permission selection
// ============================================================================

interface PermissionProfile {
  label: string;
  description: string;
  features: FeatureId[] | "custom";
}

const PERMISSION_PROFILES: PermissionProfile[] = [
  {
    label: "Everyday automation (recommended)",
    description:
      "Build, run, and fix flows; manage connections; work with SharePoint and Excel files. The right choice for most people — and a smaller permission request for your IT admin to approve.",
    features: ["powerAutomate", "powerApps", "graph"],
  },
  {
    label: "Everything",
    description:
      "All tools, including Dynamics 365 / Dataverse data, tenant administration, and Power Pages. The largest permission request.",
    features: DEFAULT_FEATURES,
  },
  {
    label: "Flows only",
    description: "Just Power Automate flows — the smallest possible permission request.",
    features: MINIMAL_FEATURES,
  },
  {
    label: "Everyday + Dynamics 365 data",
    description:
      "Everything in Everyday automation, plus reading and updating Dynamics 365 / Dataverse records, solutions, and approvals.",
    features: ["powerAutomate", "powerApps", "graph", "dataverse"],
  },
  {
    label: "Power Pages",
    description:
      "Flows plus Power Pages site configuration (includes the Dataverse access it needs).",
    features: ["powerAutomate", "dataverse", "powerPages"],
  },
  {
    label: "Custom",
    description: "Pick each capability one by one.",
    features: "custom",
  },
];

async function askYesNo(
  askUser: (question: string) => Promise<string>,
  question: string,
  defaultYes = false,
): Promise<boolean> {
  const suffix = defaultYes ? "[Y/n]" : "[y/N]";
  const answer = await askUser(`  ${c.cyan}?${c.reset} ${question} ${c.dim}${suffix}${c.reset}: `);
  if (!answer) return defaultYes;
  return /^y(es)?$/i.test(answer);
}

async function promptForCustomFeatures(
  askUser: (question: string) => Promise<string>,
): Promise<FeatureId[]> {
  const selected = new Set<FeatureId>(MINIMAL_FEATURES);
  const optionalFeatures: FeatureId[] = [
    "graph",
    "powerApps",
    "dataverse",
    "admin",
    "powerPages",
    "powerPagesManagement",
  ];

  print("");
  print(`  ${icons.check} ${FEATURE_LABELS.powerAutomate} is always included.`);
  print(`     ${c.dim}${FEATURE_DESCRIPTIONS.powerAutomate}${c.reset}`);
  print("");

  for (const feature of optionalFeatures) {
    const yes = await askYesNo(askUser, `Enable ${FEATURE_LABELS[feature]}?`, false);
    if (yes) selected.add(feature);
  }

  return normalizeFeatures([...selected]);
}

function printFeatureSelection(features: readonly FeatureId[]): void {
  const normalized = normalizeFeatures(features);
  print("");
  print(`  ${icons.check} Selected: ${c.green}${formatFeatureList(normalized)}${c.reset}`);
  print("");
  print(`  ${c.dim}The app registration will request:${c.reset}`);
  for (const line of getPermissionSummariesForFeatures(normalized, { includeManual: true })) {
    print(`    ${c.dim}• ${line}${c.reset}`);
  }
  if (normalized.includes("powerApps")) {
    print("");
    print(
      `  ${icons.warn} ${c.yellow}Creating and repairing connections needs the Power Platform API permission.${c.reset}`,
    );
    print(
      `  ${c.dim}It requires admin consent — re-running --setup later will not grant it.${c.reset}`,
    );
    print(
      `  ${c.dim}Without it, listing connections still works; create/test/fix/delete do not.${c.reset}`,
    );
  }
  if (normalized.includes("powerPagesManagement")) {
    print("");
    print(`  ${icons.warn} ${c.yellow}Power Pages hosting management is selected.${c.reset}`);
    print(
      `  ${c.dim}Setup resolves the exact PowerPages.Websites.Read/Write scope IDs through Azure CLI.${c.reset}`,
    );
    print(
      `  ${c.dim}For a manually managed app registration, add those two delegated scopes in Entra.${c.reset}`,
    );
  }
  if (normalized.includes("dataverse")) {
    print("");
    print(
      `  ${c.dim}Copilot inventory, authenticated execution, evaluations, and governance use the Power Platform API.${c.reset}`,
    );
    print(
      `  ${c.dim}Setup resolves the five delegated scopes' tenant-local IDs through Azure CLI; an Entra admin must grant consent.${c.reset}`,
    );
  }
}

async function promptForFeatures(
  askUser: (question: string) => Promise<string>,
): Promise<FeatureId[]> {
  const existing = readExistingAuthConfig().features;
  if (existing) {
    print(`  ${c.dim}Existing config enabled: ${formatFeatureList(existing)}${c.reset}`);
    const keep = await askYesNo(askUser, "Keep this permission set?", true);
    if (keep) {
      printFeatureSelection(existing);
      return existing;
    }
    print("");
  }

  PERMISSION_PROFILES.forEach((profile, index) => {
    print(`  ${c.cyan}${index + 1}${c.reset}  ${profile.label}`);
    print(`     ${c.dim}${profile.description}${c.reset}`);
  });
  print("");

  const selection = await askUser(
    `  ${c.cyan}?${c.reset} Choose permission set ${c.dim}(1-${PERMISSION_PROFILES.length})${c.reset} [${c.cyan}1${c.reset}]: `,
  );
  const index = selection ? parseInt(selection, 10) - 1 : 0;
  const profile = PERMISSION_PROFILES[index] ?? PERMISSION_PROFILES[0]!;

  const features =
    profile.features === "custom"
      ? await promptForCustomFeatures(askUser)
      : normalizeFeatures(profile.features);

  printFeatureSelection(features);
  return features;
}

// ============================================================================
// App Registration Resolution
// ============================================================================

interface AppResult {
  clientId: string;
  created: boolean;
  /** True when the Azure CLI is signed in and can run provisioning repairs. */
  viaAzureCli: boolean;
  /** Tenant pinned by an organization-provisioned org.json, when present. */
  tenantId?: string | null;
  /**
   * Feature set for this run. Only the create path asks — permissions shape
   * a NEW registration. Every other path inherits the existing config's
   * features or the defaults, because the app's consented permissions were
   * fixed by whoever provisioned it.
   */
  features: readonly FeatureId[];
  /**
   * True when the user explicitly chose the create path (even if it resumed
   * or reused an app by name): run provisioning hardening and attempt the
   * tenant-wide consent grant up front. Detection/paste paths skip both —
   * repairs and consent happen on demand if sign-in proves they're needed.
   */
  provision: boolean;
}

/**
 * Run the tenant-provisioning hardening (public client flag, client SP,
 * resource SPs, scope-id resolution + RRA PATCH) and narrate the outcome.
 * Most provisioning diagnostics remain best-effort. Missing required dynamic
 * Power Pages scopes are different: there are no safe static ids, so that
 * condition must stop setup before it can claim success with a broken app.
 */
async function runProvisioningHardening(
  clientId: string,
  features: readonly FeatureId[],
): Promise<TenantProvisioningResult | null> {
  try {
    print(
      `  ${c.dim}Verifying tenant provisioning (service principals, permission ids)...${c.reset}`,
    );
    const result = await provisionTenantForApp(clientId, features);

    if (result.clientSpCreated) {
      print(`  ${icons.check} Created the app's service principal in this tenant`);
    }
    for (const resolution of result.report.resolutions) {
      if (resolution.createdServicePrincipal) {
        print(`  ${icons.check} Created service principal for ${resolution.api}`);
      }
      if (resolution.usedSingleScopeFallback) {
        const resolvedList = resolution.resolved.map((r) => r.value).join(", ");
        print(`  ${icons.check} ${resolution.api}: resolved tenant-local scope (${resolvedList})`);
      }
    }
    for (const warning of result.report.warnings) {
      print(`  ${icons.warn} ${c.yellow}${warning}${c.reset}`);
    }
    if (!result.propagated) {
      print(
        `  ${icons.warn} ${c.dim}Permission changes are still propagating — sign-in may need a retry.${c.reset}`,
      );
    } else {
      print(`  ${icons.check} Tenant provisioning verified`);
    }
    return result;
  } catch (error) {
    if (error instanceof RequiredScopeResolutionError) {
      print(`  ${icons.cross} ${c.red}${error.message}${c.reset}`);
      throw error;
    }
    const msg = error instanceof Error ? error.message : String(error);
    print(`  ${icons.warn} ${c.yellow}Provisioning check skipped: ${msg}${c.reset}`);
    print(`  ${c.dim}Continuing with the default permission configuration.${c.reset}`);
    return null;
  }
}

/**
 * Print a copy-paste message the user can send their IT team, so a non-admin
 * gets an explicit, guided handoff instead of a dead stop. Two stages:
 * "clientId" (no app exists yet) and "consent" (app exists, approval needed).
 */
function printItRequestPack(stage: "clientId" | "consent", consentUrl?: string): void {
  print("");
  print(`  ${c.bold}Copy-paste this to your IT team:${c.reset}`);
  print(`  ${c.dim}${"─".repeat(58)}${c.reset}`);
  print(`  Hi! I'm setting up "Power Automate MCP" — a tool that lets an AI`);
  print(`  assistant work with Power Automate under my own account (it can`);
  print(`  never do more than my account can). I need your help with:`);
  if (stage === "clientId") {
    print(`   1. A Microsoft Entra app registration for it. Easiest way: run`);
    print(`      "powerautomate-mcp --setup" on a machine where the Azure CLI`);
    print(`      is signed in as an admin — it creates the app automatically.`);
    print(
      `      Manual steps: https://github.com/rcb0727/powerplatform-mcp-docs/blob/main/INSTALL.md`,
    );
    print(`   2. Send me the app's Client ID.`);
    print(`   3. Grant admin consent for it (setup shows the approval link).`);
  } else {
    print(`   Approving admin consent for the app at this link (any Global,`);
    print(`   Application, or Cloud Application Administrator can approve):`);
    if (consentUrl) print(`   ${consentUrl}`);
  }
  print(`  ${c.dim}${"─".repeat(58)}${c.reset}`);
  print("");
  print(`  ${c.dim}Your progress is saved — once IT comes back to you, just run${c.reset}`);
  print(
    `  ${c.dim}${c.reset}${c.cyan}powerautomate-mcp --setup${c.reset}${c.dim} again and it picks up where you left off.${c.reset}`,
  );
}

/**
 * The single question the wizard is allowed to ask when nothing was
 * detected: paste a Client ID, or press Enter to create a new registration.
 * Pasted IDs are probed against the tenant immediately (unauthenticated
 * device-code probe) so a typo fails here in seconds, not later at the
 * sign-in screen. Returns:
 *   { kind: "paste", clientId }  — a validated, existing app
 *   { kind: "create" }           — user pressed Enter
 *   { kind: "quit" }             — user typed q; the IT request pack prints
 */
async function promptForClientIdOrCreate(
  askUser: (question: string) => Promise<string>,
): Promise<{ kind: "paste"; clientId: string } | { kind: "create" } | { kind: "quit" }> {
  print("");
  print(`  ${c.dim}No existing setup found. This tool runs under a Microsoft Entra app${c.reset}`);
  print(`  ${c.dim}registration your organization owns.${c.reset}`);
  print("");

  while (true) {
    const input = await askUser(
      `  ${c.cyan}?${c.reset} Paste your app's ${c.bold}Client ID${c.reset}, or press ${c.bold}Enter${c.reset} to create a new one ${c.dim}(q to quit)${c.reset}: `,
    );
    if (!input) {
      return { kind: "create" };
    }
    if (/^q(uit)?$/i.test(input)) {
      printItRequestPack("clientId");
      return { kind: "quit" };
    }
    if (!UUID_RE.test(input)) {
      print(
        `  ${c.red}That doesn't look like a Client ID — it's a UUID (e.g., 12345678-abcd-1234-abcd-123456789abc).${c.reset}`,
      );
      continue;
    }
    // Verify the app actually exists before going anywhere near sign-in, so
    // a typo fails here in seconds. The probe is unauthenticated and fails
    // open: only a definitive "no such app" answer rejects the ID —
    // AADSTS700016 (tenant-specific) or AADSTS50059 (the /organizations
    // endpoint's version of the same fact). Anything else (Conditional
    // Access blocks, proxies, single-tenant apps) passes through and sign-in
    // renders the verdict.
    process.stdout.write(`  ${c.dim}Checking that app exists...${c.reset}`);
    const probe = await probeDeviceCodeEndpoint(input, null);
    process.stdout.write("\r" + " ".repeat(48) + "\r");
    if (probe?.appNotFound || probe?.unresolvable) {
      print(
        `  ${c.red}Microsoft reports no app with that ID — check for a typo or ask IT.${c.reset}`,
      );
      continue;
    }
    if (probe?.ok) {
      print(`  ${icons.check} Client ID verified: ${c.dim}${input}${c.reset}`);
    } else {
      print(
        `  ${icons.check} Client ID accepted ${c.dim}(couldn't verify from here — sign-in will confirm): ${input}${c.reset}`,
      );
    }
    return { kind: "paste", clientId: input };
  }
}

/**
 * Build the tenant admin-consent URL. Returns null when the tenant part
 * would be unsafe to place in a URL (defense against config injection).
 */
function buildAdminConsentUrl(clientId: string, tenantId: string | null): string | null {
  const tenantPart = tenantId ?? "organizations";
  if (!UUID_RE.test(tenantPart) && !["common", "organizations", "consumers"].includes(tenantPart)) {
    return null;
  }
  return (
    `https://login.microsoftonline.com/${tenantPart}/adminconsent` +
    `?client_id=${encodeURIComponent(clientId)}` +
    `&redirect_uri=${encodeURIComponent("https://login.microsoftonline.com/common/oauth2/nativeclient")}`
  );
}

/**
 * Read-only Azure CLI availability: true only when az is installed AND
 * already signed in. NEVER triggers `az login` — outside the explicit
 * create path, setup must not throw a browser at anyone.
 */
async function azCliSignedInReadOnly(): Promise<boolean> {
  if (!(await checkAzureCli())) return false;
  try {
    const output = await runAzCommand(["account", "show", "--output", "json"]);
    const account = JSON.parse(output) as { user?: { name?: string } };
    return Boolean(account.user?.name);
  } catch {
    return false;
  }
}

/**
 * Resolve the app registration. Detection first — the wizard figures out
 * which situation it's in and only asks when it genuinely can't know:
 *   1. PA_MCP_CLIENT_ID environment variable
 *   2. Existing config file (probed — a deleted app doesn't sail through)
 *   3. Organization-provisioned org.json (IT pushed via Intune/GPO/Jamf)
 *   4. Azure CLI, READ-ONLY: only if az is already signed in, look the app
 *      up by name and offer it. Never triggers `az login`.
 *   5. The one question: paste a Client ID, or press Enter to create.
 * `az login` and app creation happen ONLY inside the explicit create path.
 */
async function resolveAppRegistration(
  askUser: (question: string) => Promise<string>,
): Promise<AppResult | null> {
  // Non-create paths inherit features: the app's consented permissions were
  // fixed by whoever provisioned it, so there is nothing to ask about.
  const inheritedFeatures: readonly FeatureId[] =
    readExistingAuthConfig().features ?? normalizeFeatures([...DEFAULT_FEATURES]);

  // 1. Environment variable
  const envClientId = process.env["PA_MCP_CLIENT_ID"];
  if (envClientId && UUID_RE.test(envClientId)) {
    print(
      `  ${icons.check} Using client ID from PA_MCP_CLIENT_ID: ${c.dim}${envClientId}${c.reset}`,
    );
    return {
      clientId: envClientId,
      created: false,
      viaAzureCli: await azCliSignedInReadOnly(),
      features: inheritedFeatures,
      provision: false,
    };
  }

  // 2. Existing config — but verify the app still exists before reusing it.
  //    An app registration deleted from the tenant (orphaned config) used to
  //    sail through here and kill setup later at sign-in with a misleading
  //    device-code error. The probe is unauthenticated and fails open: only a
  //    definitive AADSTS700016 skips reuse.
  const existingId = readExistingClientId();
  if (existingId) {
    const probe = await probeDeviceCodeEndpoint(existingId, readExistingAuthConfig().tenantId);
    if (probe?.appNotFound) {
      print(
        `  ${c.yellow}⚠${c.reset} The app in your existing config (${c.dim}${existingId}${c.reset}) no longer exists in this tenant — it may have been deleted.`,
      );
      print(`  ${c.dim}Continuing so you can create or enter a new one.${c.reset}`);
    } else {
      print(`  ${icons.check} Reusing app from existing config: ${c.dim}${existingId}${c.reset}`);
      return {
        clientId: existingId,
        created: false,
        viaAzureCli: await azCliSignedInReadOnly(),
        features: inheritedFeatures,
        provision: false,
      };
    }
  }

  // 3. Organization-provisioned config — IT created the app once and pushed
  //    an org.json to a machine-wide path; use it with nothing to type. No
  //    Azure CLI involvement: consent was already granted by whoever
  //    provisioned the app, and triggering `az login` here would throw a
  //    browser at exactly the users this path exists to spare.
  const orgConfig = readOrgConfig();
  if (orgConfig) {
    print(
      `  ${icons.check} Found your organization's setup file: ${c.dim}${orgConfig.source}${c.reset}`,
    );
    print(
      `  ${icons.check} Using your organization's app registration: ${c.dim}${orgConfig.clientId}${c.reset}`,
    );
    return {
      clientId: orgConfig.clientId,
      created: false,
      viaAzureCli: false,
      tenantId: orgConfig.tenantId,
      features: inheritedFeatures,
      provision: false,
    };
  }

  // 4. Read-only Azure CLI detection — az already signed in, so a directory
  //    lookup costs nothing and needs no browser.
  if (await azCliSignedInReadOnly()) {
    // A previous interrupted create run may have left an app behind — resume
    // it instead of stranding an orphan.
    const state = readSetupState();
    if (state.createdClientId && (await appRegistrationExists(state.createdClientId))) {
      print(
        `  ${icons.check} Resuming with app created by a previous run: ${c.dim}${state.createdClientId}${c.reset}`,
      );
      return {
        clientId: state.createdClientId,
        created: false,
        viaAzureCli: true,
        features: inheritedFeatures,
        provision: false,
      };
    }

    const foundAppId = await findExistingApp(APP_DISPLAY_NAME);
    if (foundAppId) {
      print(
        `  ${icons.check} Found ${c.bold}${APP_DISPLAY_NAME}${c.reset} already registered in your organization: ${c.dim}${foundAppId}${c.reset}`,
      );
      const useIt = await askYesNo(askUser, "Use it?", true);
      if (useIt) {
        return {
          clientId: foundAppId,
          created: false,
          viaAzureCli: true,
          features: inheritedFeatures,
          provision: false,
        };
      }
    }
  }

  // 5. Nothing detected — the one question.
  const answer = await promptForClientIdOrCreate(askUser);
  if (answer.kind === "quit") {
    return null; // IT handoff — the request pack was printed
  }
  if (answer.kind === "paste") {
    return {
      clientId: answer.clientId,
      created: false,
      viaAzureCli: await azCliSignedInReadOnly(),
      features: inheritedFeatures,
      provision: false,
    };
  }
  return runCreateAppFlow(askUser);
}

/**
 * The create path — the ONLY place `az login`, app creation, and tenant-wide
 * admin consent happen, because the user explicitly chose it. Permission
 * sets are asked here (they shape the new registration) with the standard
 * profile preselected; consent is attempted automatically — creating an app
 * for org use IS the request for org-wide consent, and if the grant fails
 * (not an admin), sign-in verification surfaces the consent URL only if
 * Microsoft actually demands it.
 */
async function runCreateAppFlow(
  askUser: (question: string) => Promise<string>,
): Promise<AppResult | null> {
  print("");
  print(`  ${c.bold}Creating your app registration${c.reset}`);
  print("");
  const features = await promptForFeatures(askUser);

  if (!(await checkAzureCli())) {
    print("");
    print(
      `  ${icons.warn} ${c.yellow}Creating the app needs the Azure CLI, which isn't installed.${c.reset}`,
    );
    print(
      `  ${c.dim}Install it (macOS: brew install azure-cli · Windows: winget install Microsoft.AzureCLI)${c.reset}`,
    );
    print(`  ${c.dim}and re-run setup — or hand this to your IT team:${c.reset}`);
    printItRequestPack("clientId");
    return null;
  }

  try {
    const azUser = await ensureAzureCliLogin();
    print(`  ${icons.check} Azure CLI signed in as ${c.bold}${azUser}${c.reset}`);
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    print(`  ${icons.warn} ${c.yellow}Azure sign-in failed: ${msg.split("\n")[0]}${c.reset}`);
    printItRequestPack("clientId");
    return null;
  }

  try {
    // A previous run may have created an app and then failed before config
    // was saved — reuse it instead of creating an orphan sibling.
    const state = readSetupState();
    if (state.createdClientId) {
      if (await appRegistrationExists(state.createdClientId)) {
        print(
          `  ${icons.check} Resuming with app created by a previous run: ${c.dim}${state.createdClientId}${c.reset}`,
        );
        return {
          clientId: state.createdClientId,
          created: false,
          viaAzureCli: true,
          features,
          provision: true,
        };
      }
      print(`  ${c.dim}Previous run's app no longer exists — clearing stale reference.${c.reset}`);
      writeSetupState({});
    }

    // Reuse by name before creating a sibling.
    const existingAppId = await findExistingApp(APP_DISPLAY_NAME);
    if (existingAppId) {
      print(
        `  ${icons.check} ${APP_DISPLAY_NAME} already exists in your tenant — reusing it: ${c.dim}${existingAppId}${c.reset}`,
      );
      print(
        `  ${c.dim}Existing app permissions are not removed automatically; adjust them in Entra if you are reducing scope.${c.reset}`,
      );
      try {
        await addWamRedirectUri(existingAppId);
      } catch {
        // non-critical — WAM URI may already exist or user lacks permission
      }
      return {
        clientId: existingAppId,
        created: false,
        viaAzureCli: true,
        features,
        provision: true,
      };
    }

    print(`  ${c.dim}Creating app registration...${c.reset}`);
    const newId = await createAppRegistration(APP_DISPLAY_NAME, features);
    // Persist IMMEDIATELY — before the fragile auth/consent steps — so a
    // failed run resumes with this app instead of creating another one.
    writeSetupState({ createdClientId: newId, createdAt: new Date().toISOString() });
    print(`  ${icons.check} App created: ${c.dim}${newId}${c.reset}`);

    try {
      await addWamRedirectUri(newId);
    } catch {
      // non-critical
    }

    return { clientId: newId, created: true, viaAzureCli: true, features, provision: true };
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    print(`  ${c.yellow}Azure CLI failed: ${msg.split("\n")[0]}${c.reset}`);
    printItRequestPack("clientId");
    return null;
  }
}

// ============================================================================
// Setup Flow
// ============================================================================

export interface SetupResult {
  success: boolean;
  configPath?: string;
  error?: string;
  /** True when setup exited cleanly to wait on IT (client ID or consent) — not a failure. */
  pending?: boolean;
}

/** Options that pre-answer or skip interactive steps. */
export interface SetupOptions {
  /** Pre-selected AI client to connect (skips the menu). "skip" connects none. */
  client?: ClientId | "skip";
  /** Wire clients to run via `npx -y powerautomate-mcp@latest` instead of the global binary. */
  useNpx?: boolean;
}

/** Ordered client menu shown when no --client was supplied. */
const CLIENT_MENU: ClientId[] = [
  "claude",
  "claude-code",
  "codex",
  "cursor",
  "vscode",
  "gemini",
  "windsurf",
];

/**
 * Step 6 — connect the user's AI client by writing/merging its MCP config so
 * they never hand-edit JSON. Auto-runs the chosen client when `opts.client` is
 * set, otherwise shows a menu. Never fails the overall setup — a missed client
 * connection just prints manual instructions.
 */
async function runConnectClientStep(
  prompt: (q: string) => Promise<string>,
  opts: SetupOptions,
): Promise<void> {
  printStep(5, 5, "Connect Your AI App");

  let choice: ClientId | "skip" | undefined = opts.client;

  if (!choice) {
    // Detect which AI apps are actually on this machine so the common case is
    // one keystroke instead of an 8-way menu. Best-effort: detection failures
    // just fall back to the full menu.
    process.stdout.write(`  ${c.dim}Looking for AI apps on this machine...${c.reset}`);
    let installed = new Set<ClientId>();
    try {
      const detections = [...detectClientConfigs(), await detectClaudeCode(), await detectCodex()];
      installed = new Set(detections.filter((d) => d.present).map((d) => d.id));
    } catch {
      // best-effort only
    }
    process.stdout.write("\r" + " ".repeat(48) + "\r");

    const detected = CLIENT_MENU.filter((id) => installed.has(id));

    if (detected.length === 1) {
      const only = detected[0]!;
      const yes = await askYesNo(
        prompt,
        `Found ${c.bold}${CLIENT_LABELS[only]}${c.reset} on this machine — connect it?`,
        true,
      );
      if (yes) {
        choice = only;
      }
    }

    if (!choice) {
      print(`  ${c.dim}I can wire this server into your AI app's config for you —${c.reset}`);
      print(`  ${c.dim}no hand-editing JSON.${c.reset}`);
      print("");
      // Detected apps first, so the default [1] is something actually installed.
      const ordered = [...detected, ...CLIENT_MENU.filter((id) => !installed.has(id))];
      ordered.forEach((id, i) => {
        const badge = installed.has(id) ? ` ${c.green}• detected${c.reset}` : "";
        print(`  ${c.cyan}${i + 1}${c.reset}  ${CLIENT_LABELS[id]}${badge}`);
      });
      print(
        `  ${c.cyan}${ordered.length + 1}${c.reset}  ${c.dim}Skip — I'll configure it myself${c.reset}`,
      );
      print("");
      const sel = await prompt(
        `  ${c.cyan}?${c.reset} Which app are you using? ${c.dim}(1-${ordered.length + 1})${c.reset} [${c.cyan}1${c.reset}]: `,
      );
      const idx = sel ? parseInt(sel, 10) - 1 : 0;
      if (Number.isNaN(idx) || idx < 0 || idx > ordered.length) {
        print(
          `  ${c.dim}Skipped — configure your client manually (see the install guide).${c.reset}`,
        );
        return;
      }
      if (idx === ordered.length) {
        choice = "skip";
      } else {
        choice = ordered[idx]!;
      }
    }
  }

  if (choice === "skip") {
    print(`  ${c.dim}Skipped. See the install guide to add the server to your client.${c.reset}`);
    return;
  }

  print("");
  process.stdout.write(`  ${c.dim}Connecting ${CLIENT_LABELS[choice]}...${c.reset}`);
  const result = await connectClient(choice, { useNpx: opts.useNpx });
  process.stdout.write("\r" + " ".repeat(48) + "\r");

  if (result.ok) {
    const verb =
      result.action === "unchanged"
        ? "already configured"
        : result.action === "updated"
          ? "updated"
          : "connected";
    print(`  ${icons.check} ${c.green}${result.label} ${verb}${c.reset}`);
    if (result.path) print(`     ${c.dim}${result.path}${c.reset}`);
    if (result.backupPath) {
      print(
        `  ${icons.warn} ${c.dim}Existing file had invalid JSON — backed up to ${result.backupPath}${c.reset}`,
      );
    }
    const info = getRestartHint(choice);
    if (info) print(`  ${icons.arrow} ${info}`);
  } else if (result.action === "manual" && result.manualCommand) {
    print(`  ${icons.warn} ${c.yellow}Couldn't auto-connect ${result.label}.${c.reset}`);
    print(`  ${c.dim}Run this yourself:${c.reset}`);
    print(`    ${c.cyan}${result.manualCommand}${c.reset}`);
  } else {
    print(
      `  ${icons.warn} ${c.yellow}Couldn't connect ${result.label}: ${result.error ?? "unknown error"}${c.reset}`,
    );
    print(`  ${c.dim}See the install guide to add the server manually.${c.reset}`);
  }
}

/** Restart hint per client (kept here to avoid leaking UI strings into the writer). */
function getRestartHint(id: ClientId | "skip"): string | null {
  switch (id) {
    case "claude":
      return "Quit and reopen Claude Desktop — the tools appear automatically.";
    case "claude-code":
      return "Run `claude` — the tools load on the next session.";
    case "codex":
      return "Start a new Codex/ChatGPT CLI session — the tools load automatically.";
    case "cursor":
      return "Restart Cursor to pick up the new server.";
    case "vscode":
      return 'Reload VS Code (Command Palette → "Developer: Reload Window").';
    case "gemini":
      return "Restart your Gemini CLI session.";
    case "windsurf":
      return "Restart Windsurf, then refresh MCP servers in settings.";
    default:
      return null;
  }
}

/**
 * Run the interactive setup wizard
 */
export async function runSetupWizard(opts: SetupOptions = {}): Promise<SetupResult> {
  quietInteractiveLogging();

  // npm's install-script policy (npm 12 blocks by default) can leave keytar
  // without its native binary, which takes the whole secure-storage layer
  // down with it — sign-in appears to work, then every step after finds an
  // empty token cache. Repair it up front, while we have a terminal.
  if (!nativeStorageLoadable()) {
    console.error("  One-time repair: npm's install-script policy blocked a native component");
    console.error("  needed for secure token storage. Rebuilding it now (10-30s)...");
    const repaired = await repairNativeStorage();
    if (repaired === "repaired") {
      console.error("  ✓ Repaired.\n");
    } else {
      console.error("  ✗ Automatic repair failed.");
      console.error(`    ${manualRepairInstruction()}`);
      console.error("  then re-run: powerautomate-mcp --setup\n");
      return { success: false, error: "native secure-storage components unavailable" };
    }
  }

  // Create readline interface ONLY when running the wizard
  // (not at module load - that would break MCP stdin transport)
  const rl = createInterface({
    input: process.stdin,
    output: process.stdout,
  });

  function prompt(question: string): Promise<string> {
    return new Promise((resolve, reject) => {
      // Without this, EOF (Ctrl-D, closed pipe) during a question left the
      // promise pending forever and the process drained to a silent exit 0 —
      // indistinguishable from success. Fail loudly instead.
      const onClose = () => reject(new Error("Setup interrupted — input closed before a response"));
      rl.once("close", onClose);
      rl.question(question, (answer) => {
        rl.removeListener("close", onClose);
        resolve(answer.trim());
      });
    });
  }

  try {
    printHeader();

    const totalSteps = 5;

    // ========================================================================
    // Step 1: Find Your App — detection first, one question only if needed.
    // Permission sets are asked inside the create path (they shape a NEW
    // registration); every other path inherits stored/default features.
    // ========================================================================
    printStep(1, totalSteps, "Find Your App");

    const appResult = await resolveAppRegistration(prompt);
    if (appResult === null) {
      rl.close();
      return { success: false, pending: true, error: "Waiting on a Client ID from IT" };
    }
    const clientId = appResult.clientId;
    const enabledFeatures = appResult.features;

    if (appResult.provision) {
      // Create path only: ensure SPs exist and permission ids match what THIS
      // tenant's service principals publish (fresh tenants otherwise fail
      // sign-in with AADSTS650052 / AADSTS65006)...
      await runProvisioningHardening(clientId, enabledFeatures);

      // ...then attempt tenant-wide admin consent without asking. Creating an
      // app for org use IS the consent request, and the user just chose the
      // permissions themselves. If they lack an admin role this fails
      // quietly — sign-in surfaces the consent URL only if Microsoft
      // actually demands it.
      try {
        process.stdout.write(
          `  ${c.dim}Granting admin consent (makes the app usable org-wide)...${c.reset}`,
        );
        await grantAdminConsent(clientId);
        process.stdout.write("\r" + " ".repeat(56) + "\r");
        print(
          `  ${icons.check} ${c.green}Admin consent granted${c.reset} ${c.dim}— everyone in your org can now sign in${c.reset}`,
        );
      } catch (error) {
        process.stdout.write("\r" + " ".repeat(56) + "\r");
        const msg = error instanceof Error ? error.message : String(error);
        print(`  ${c.dim}Tenant-wide consent not granted (${msg.split("\n")[0]}).${c.reset}`);
        print(
          `  ${c.dim}No problem — sign-in will show the approval link only if it's required.${c.reset}`,
        );
      }
    }

    // ========================================================================
    // Step 2: Sign In — consent is VERIFIED here, not asked about. If
    // Microsoft returns AADSTS65001, the consent URL appears; otherwise
    // nobody reads a consent paragraph.
    // ========================================================================
    printStep(2, totalSteps, "Sign In");

    // Use the tenant pinned by an org.json first, then the tenant from the
    // existing config (either prevents invalid_grant with /common).
    const existingAuth = readExistingAuthConfig();
    let tenantId: string | null = appResult.tenantId ?? existingAuth.tenantId;

    // Clear cached tokens ONLY when the app changed or was just provisioned
    // (fresh permissions to pick up). Re-running setup against the same app
    // keeps a working sign-in — the silent acquire below succeeds and nobody
    // re-enters a device code just for running the wizard twice.
    const appChanged = appResult.provision || existingAuth.clientId !== clientId;
    if (appChanged) {
      const cacheResult = await clearTokenCache();
      if (cacheResult.cleared) {
        print(`  ${c.dim}Cleared cached credentials (${cacheResult.files.join(", ")})${c.reset}`);
      }
      print(`  ${c.dim}Sign in with your Microsoft work account.${c.reset}`);
      print(
        `  ${c.dim}A device code will be shown — follow the instructions to authenticate.${c.reset}`,
      );
    } else {
      print(
        `  ${c.dim}Same app as your existing setup — keeping your current sign-in if it's still valid.${c.reset}`,
      );
    }
    print("");

    let authProvider = await createAuthProviderFromConfig(clientId, tenantId);

    try {
      // Authenticate with Flow API (primary scope), retrying provisioning-
      // related failures: app-registration changes take minutes to propagate
      // to the sign-in service (AADSTS65006 with the OLD ids is propagation,
      // not a failed fix), and missing SPs (AADSTS650052) are repairable on
      // the spot when the Azure CLI is signed in.
      const PROPAGATION_BACKOFFS_MS = [15_000, 30_000, 60_000];
      let attempt = 0;
      let repaired = false;
      let consentRemediated = false;
      for (;;) {
        try {
          await authProvider.getToken();
          break;
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          const kind = classifyProvisioningError(message);

          if (kind === "app-not-found") {
            print(
              `  ${icons.cross} ${c.red}The configured app registration no longer exists in this tenant.${c.reset}`,
            );
            print(
              `  ${c.dim}Delete ${getConfigPath()} (or clear auth.clientId) and re-run --setup to create a new one.${c.reset}`,
            );
            throw error;
          }

          if (kind === "missing-sp" && appResult.viaAzureCli && !repaired) {
            print(
              `  ${icons.warn} ${c.yellow}A required service principal is missing in this tenant — repairing...${c.reset}`,
            );
            repaired = true;
            await runProvisioningHardening(clientId, enabledFeatures);
            continue;
          }

          if (kind === "bad-permission-ids" && attempt < PROPAGATION_BACKOFFS_MS.length) {
            const waitMs = PROPAGATION_BACKOFFS_MS[attempt]!;
            attempt += 1;
            print(
              `  ${c.dim}App registration changes are still propagating (this is normal, not a failure).${c.reset}`,
            );
            print(
              `  ${c.dim}Retrying sign-in in ${Math.round(waitMs / 1000)}s... (attempt ${attempt}/${PROPAGATION_BACKOFFS_MS.length})${c.reset}`,
            );
            await new Promise((resolve) => setTimeout(resolve, waitMs));
            continue;
          }

          if (kind === "consent-required") {
            // Microsoft actually demanded admin consent — NOW it's worth
            // talking about. One silent az repair attempt first, then the
            // approval URL for a human.
            print("");
            print(
              `  ${icons.warn} ${c.yellow}Your organization hasn't approved this app's permissions yet.${c.reset}`,
            );

            if (appResult.viaAzureCli && !consentRemediated) {
              consentRemediated = true;
              try {
                process.stdout.write(
                  `  ${c.dim}Trying to grant admin consent via Azure CLI...${c.reset}`,
                );
                await grantAdminConsent(clientId);
                process.stdout.write("\r" + " ".repeat(52) + "\r");
                print(
                  `  ${icons.check} ${c.green}Admin consent granted${c.reset} ${c.dim}— retrying sign-in${c.reset}`,
                );
                continue;
              } catch {
                process.stdout.write("\r" + " ".repeat(52) + "\r");
                print(`  ${c.dim}Couldn't grant it from here (needs an admin role).${c.reset}`);
              }
            }

            const adminConsentUrl = buildAdminConsentUrl(clientId, tenantId);
            if (!adminConsentUrl) {
              throw error;
            }

            print("");
            print(
              `  ${c.bold}Approval link${c.reset} ${c.dim}(any Global, Application, or Cloud Application Admin can approve):${c.reset}`,
            );
            print(`  ${c.cyan}${adminConsentUrl}${c.reset}`);
            print("");
            const ans = await prompt(
              `  ${c.cyan}?${c.reset} Press ${c.bold}Enter${c.reset} to retry sign-in once it's approved, or type ${c.bold}q${c.reset} to finish later: `,
            );
            if (/^q/i.test(ans)) {
              printItRequestPack("consent", adminConsentUrl);
              rl.close();
              return { success: false, pending: true, error: "Waiting on admin consent from IT" };
            }
            continue;
          }

          throw error;
        }
      }
      const account = authProvider.getCurrentAccount();
      if (account) {
        const learnedTenant = account.tenantId ?? null;
        print(
          `  ${icons.check} ${c.green}Signed in${c.reset} as ${c.bold}${account.username}${c.reset}`,
        );
        // The provider was built BEFORE we knew the tenant, so its authority
        // is /common — where silent redemption fails for some accounts even
        // though the same refresh token works tenant-specifically. Rebuild it
        // with the tenant we just learned (config only stores tenantId at the
        // END of setup, so without this every post-sign-in call — secondary-
        // resource authorization and environment discovery — stays on /common).
        if (learnedTenant && learnedTenant !== tenantId) {
          tenantId = learnedTenant;
          authProvider = await createAuthProviderFromConfig(clientId, tenantId);
        } else if (learnedTenant) {
          tenantId = learnedTenant;
        }
      } else {
        print(`  ${icons.check} ${c.green}Signed in${c.reset}`);
      }
      // Device-code auth completes by polling — the browser leg has no
      // redirect target, so AAD may park the tab on ".../common/wrongplace".
      // That page is NOT an error; tell the user so they don't read it as one.
      print(`  ${c.dim}Authentication complete — you can close the sign-in browser tab${c.reset}`);
      print(
        `  ${c.dim}(a leftover "wrongplace" page there is normal for device-code sign-in).${c.reset}`,
      );

      // Acquire tokens for additional resources needed at runtime.
      // BAP token is critical for Dataverse URL resolution — use interactive
      // auth (getTokenForResource) so consent is captured, not just silent probe.
      // PowerApps token is needed for list_connections.
      const featureSet = new Set(enabledFeatures);
      const additionalResources = [
        {
          url: "https://graph.microsoft.com",
          label: "Microsoft Graph (SharePoint/Excel)",
          critical: true,
          enabled: featureSet.has("graph"),
        },
        {
          url: "https://service.powerapps.com",
          label: "PowerApps (connections)",
          critical: true,
          enabled: featureSet.has("powerApps"),
        },
        {
          url: "https://api.bap.microsoft.com",
          label: "Admin API (environments/Dataverse)",
          critical: true,
          enabled:
            featureSet.has("dataverse") || featureSet.has("admin") || featureSet.has("powerPages"),
        },
        {
          url: "https://api.powerplatform.com",
          label: "Power Platform API (Copilot Studio / Power Pages management)",
          critical: false,
          enabled: featureSet.has("dataverse") || featureSet.has("powerPagesManagement"),
        },
      ].filter((resource) => resource.enabled);
      for (const { url, label, critical } of additionalResources) {
        try {
          // Try silent first
          let token: string | null = null;
          if (authProvider.tryGetTokenForResourceSilent) {
            token = await authProvider.tryGetTokenForResourceSilent(url);
          }
          if (token) {
            print(`  ${icons.check} ${c.dim}${label}: authorized${c.reset}`);
          } else {
            // Silent failed — use interactive auth to get consent
            print(`  ${c.dim}Authorizing ${label}...${c.reset}`);
            await authProvider.getTokenForResource(url);
            print(`  ${icons.check} ${c.dim}${label}: authorized (interactive)${c.reset}`);
          }
        } catch {
          if (critical) {
            print(
              `  ${c.yellow || ""}${icons.warn} ${label}: not authorized — Dataverse tools may not work.${c.reset}`,
            );
            print(
              `  ${c.dim}  Add dataverseUrl to your environment config as a workaround.${c.reset}`,
            );
          } else {
            print(
              `  ${c.dim}${icons.info || "-"} ${label}: not authorized (non-critical)${c.reset}`,
            );
          }
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      print(`  ${icons.cross} ${c.red}Sign-in failed:${c.reset} ${message}`);
      rl.close();
      return { success: false, error: `Authentication failed: ${message}` };
    }

    // ========================================================================
    // Step 3: Select Environment
    // ========================================================================
    printStep(3, totalSteps, "Select Environment");
    process.stdout.write(`  ${c.dim}Discovering environments...${c.reset}`);

    let environments: DiscoveredEnvironment[] | null = null;
    let sharedConsentUrl = false;
    while (environments === null) {
      try {
        environments = await discoverEnvironments(authProvider);
        process.stdout.write("\r" + " ".repeat(40) + "\r"); // Clear line
      } catch (error) {
        process.stdout.write("\r" + " ".repeat(40) + "\r");
        const message = error instanceof Error ? error.message : String(error);

        // Check if this is a consent-related failure
        const isConsentError =
          message.includes("AADSTS65001") ||
          message.includes("consent") ||
          message.includes("approval") ||
          message.includes("admin");

        if (!isConsentError) {
          print(`  ${icons.cross} ${c.red}Failed:${c.reset} ${message}`);
          rl.close();
          return { success: false, error: `Environment discovery failed: ${message}` };
        }

        print(`  ${icons.cross} ${c.yellow}Admin consent has not been granted yet.${c.reset}`);
        if (!sharedConsentUrl) {
          const discoveryConsentUrl = buildAdminConsentUrl(clientId, tenantId);
          if (discoveryConsentUrl) {
            print("");
            print(`  ${c.bold}Share this URL with your admin:${c.reset}`);
            print(`  ${c.cyan}${discoveryConsentUrl}${c.reset}`);
            print("");
          }
          sharedConsentUrl = true;
        }
        const ans = await prompt(
          `  ${c.cyan}?${c.reset} Press ${c.bold}Enter${c.reset} to re-check, or type ${c.bold}q${c.reset} to finish later: `,
        );
        if (/^q/i.test(ans)) {
          print(
            `  ${c.dim}Once approved, run ${c.cyan}powerautomate-mcp --setup${c.dim} again — your progress is saved.${c.reset}`,
          );
          rl.close();
          return { success: false, pending: true, error: "Waiting on admin consent from IT" };
        }
        process.stdout.write(`  ${c.dim}Checking again...${c.reset}`);
      }
    }

    if (environments.length === 0) {
      print(`  ${icons.cross} ${c.red}No environments found.${c.reset}`);
      print(`  ${c.dim}You may not have access to Power Automate.${c.reset}`);
      rl.close();
      return { success: false, error: "No environments found" };
    }

    print(`  ${c.dim}Found ${environments.length} environment(s):${c.reset}`);
    print("");

    const defaultEnv = getDefaultEnvironment(environments);

    environments.forEach((env, index) => {
      const isDefault = env.id === defaultEnv?.id;
      const num = `${c.cyan}${index + 1}${c.reset}`;
      const name = isDefault ? `${c.bold}${env.displayName}${c.reset}` : env.displayName;
      const badge = isDefault ? ` ${icons.star} ${c.yellow}recommended${c.reset}` : "";
      const meta = `${c.dim}${env.type} • ${env.region}${c.reset}`;
      print(`  ${num}  ${name}${badge}`);
      print(`     ${meta}`);
    });

    print("");

    // Let user select
    let selectedEnv: DiscoveredEnvironment;

    if (environments.length === 1) {
      selectedEnv = environments[0]!;
      print(`  ${icons.arrow} Auto-selected: ${c.bold}${selectedEnv.displayName}${c.reset}`);
    } else {
      const defaultIndex = defaultEnv ? environments.indexOf(defaultEnv) + 1 : 1;
      const selection = await prompt(
        `  ${c.cyan}?${c.reset} Select environment ${c.dim}(1-${environments.length})${c.reset} [${c.cyan}${defaultIndex}${c.reset}]: `,
      );
      const index = selection ? parseInt(selection, 10) - 1 : defaultIndex - 1;

      if (index < 0 || index >= environments.length) {
        print(`  ${icons.cross} Invalid selection.`);
        rl.close();
        return { success: false, error: "Invalid environment selection" };
      }

      selectedEnv = environments[index]!;
    }

    print(`  ${icons.check} Selected: ${c.green}${selectedEnv.displayName}${c.reset}`);

    // ========================================================================
    // Step 4: Save Configuration
    // ========================================================================
    printStep(4, totalSteps, "Save Configuration");

    const configPath = getConfigPath();
    const configDir = dirname(configPath);

    if (!existsSync(configDir)) {
      mkdirSync(configDir, { recursive: true });
    }

    const config = {
      auth: {
        clientId,
        tenantId: tenantId ?? null,
      },
      features: {
        enabled: enabledFeatures,
      },
      environments: {
        default: "main",
        main: {
          id: selectedEnv.id,
          region: selectedEnv.region,
          displayName: selectedEnv.displayName,
        },
      },
      cache: {
        schemaPath: null,
        refreshIntervalHours: 24,
      },
      logging: {
        level: "info",
      },
    };

    writeFileSync(configPath, JSON.stringify(config, null, 2), { encoding: "utf-8", mode: 0o600 });
    print(`  ${icons.check} Configuration saved`);
    print(`     ${c.dim}${configPath}${c.reset}`);

    // The config now owns the clientId — clear the resume-state breadcrumb.
    writeSetupState({});

    // ========================================================================
    // Step 5: Connect AI Client (never fails overall setup)
    // ========================================================================
    try {
      await runConnectClientStep(prompt, opts);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      print(
        `  ${icons.warn} ${c.dim}Client connection skipped (${msg}). Configure manually if needed.${c.reset}`,
      );
    }

    // ========================================================================
    // Final verification — prove the setup works the way the MCP server will
    // actually use it: a FRESH silent-only auth provider (config + persisted
    // token cache only, no interactive fallback) must produce a Flow token.
    // Catches persistence failures before the user switches apps and hits a
    // confusing error there.
    // ========================================================================
    print("");
    process.stdout.write(`  ${c.dim}Verifying everything works...${c.reset}`);
    try {
      const { loadConfig } = await import("../config/loader.js");
      loadConfig(); // the just-written file must parse and validate
      const { RESOURCES } = await import("../auth/types.js");
      const verifyProvider = await createAuthProviderFromConfig(
        clientId,
        tenantId,
        undefined,
        true, // silentOnly — exactly what the MCP server does at launch
      );
      await verifyProvider.getTokenForResource(RESOURCES.FLOW_SERVICE);
      process.stdout.write("\r" + " ".repeat(40) + "\r");
      print(
        `  ${icons.check} ${c.green}Verified${c.reset} ${c.dim}— config valid, sign-in persisted, Power Automate reachable${c.reset}`,
      );
    } catch (err) {
      process.stdout.write("\r" + " ".repeat(40) + "\r");
      const msg = err instanceof Error ? err.message : String(err);
      print(
        `  ${icons.warn} ${c.yellow}Setup finished, but verification hit a snag:${c.reset} ${c.dim}${msg.split("\n")[0]}${c.reset}`,
      );
      print(
        `  ${c.dim}Run ${c.cyan}powerautomate-mcp --doctor${c.dim} for a full diagnosis.${c.reset}`,
      );
    }

    // Done!
    print("");
    print(
      `${c.green}${c.bold}  ╔══════════════════════════════════════════════════════════════╗${c.reset}`,
    );
    print(
      `${c.green}${c.bold}  ║${c.reset}                                                              ${c.green}${c.bold}║${c.reset}`,
    );
    print(
      `${c.green}${c.bold}  ║${c.reset}   ${icons.check} ${c.bold}Setup Complete!${c.reset}                                       ${c.green}${c.bold}║${c.reset}`,
    );
    print(
      `${c.green}${c.bold}  ║${c.reset}                                                              ${c.green}${c.bold}║${c.reset}`,
    );
    print(
      `${c.green}${c.bold}  ╚══════════════════════════════════════════════════════════════╝${c.reset}`,
    );
    print("");
    print(`  ${c.dim}You're ready to use Power Automate with your AI app.${c.reset}`);
    print("");
    print(`  ${c.bold}Next steps:${c.reset}`);
    print(`  ${icons.bullet} Restart your AI app so it loads the new tools`);
    print(
      `  ${icons.bullet} Confirm everything works: ${c.cyan}powerautomate-mcp --doctor${c.reset}`,
    );
    print(`  ${icons.bullet} Then just ask it to build or manage a flow`);
    if (enabledFeatures.includes("powerApps")) {
      print(
        `  ${icons.bullet} Canvas source authoring (preview): install .NET SDK 10+, open the app in Studio, enable coauthoring, and keep that tab open`,
      );
    }
    print("");
    print(
      `  ${c.bold}Rolling this out to your team?${c.reset} Run ${c.cyan}powerautomate-mcp --emit-org-config${c.reset}`,
    );
    print(
      `  ${c.dim}and push the file machine-wide — everyone else just signs in from chat.${c.reset}`,
    );
    print("");

    rl.close();
    return { success: true, configPath };
  } catch (error) {
    rl.close();
    const message = error instanceof Error ? error.message : String(error);
    return { success: false, error: message };
  }
}

/**
 * Check if setup is needed — false when the server can already run from any
 * config source (file, PA_MCP_* env vars, or an org.json with an environment
 * pin). Auto-running the wizard with one of those present would break MCP
 * stdio mode: readline would claim stdin, the protocol channel.
 */
export function isSetupNeeded(): boolean {
  return !hasRunnableConfigSource();
}
